<template>
  <div class="overall-grade-container">
    <!-- Selected BaseData Forms -->
    <div v-if="selectedBaseDataQuestionnaires.length > 0">
      <p class="caption">{{ $t('bf.accompaniments') }}</p>
      <div
          v-for="(rating, index) in filteredRatings"
          :key="`baseDataRating-${index}`"
          class="topic-container"
          :class="{ 'list-odd-highlight': index % 2 !== 0 }"
      >
        <div class="d-flex justify-space-between align-items-center font-italic" style="width: 100%">
          <p>{{ rating.title }}</p>
          <p>{{ rating.grade }}</p>
        </div>
      </div>
      <div class="topic-container mb-5 mt-2">
        <v-divider></v-divider>
        <div class="d-flex justify-space-between mt-2">
          <p>{{ $t('bf.averageAccompaniments') }}</p>
          <p class="font-weight-black">{{averageAccompaniments.toFixed(2)}}</p>
        </div>
        </div>
    </div>

    <!-- Current MAG Form -->
    <div v-for="section in sections" :key="`grade-section-id-${section.id}`" class="section-container mb-5">
      <p class="caption">{{ section.title }}</p>
      <div
        v-for="(topic, index) in section.topics"
        :key="`grade-topic-id-${topic.id}`"
        class="d-flex justify-space-between topic-container"
        :class="{ 'list-odd-highlight': index % 2 !== 0 }"
      >
        <p>{{ topic.title }}</p>
        <p class="font-weight-black">{{
            averageNumericAnswers(topic.id) !== null ? averageNumericAnswers(topic.id).toFixed(2) : '-'
          }}</p>
      </div>
    </div>

    <!-- Total Sum -->
    <v-divider></v-divider>
    <div class="d-flex justify-space-between topic-container">
      <p class="font-weight-black">{{ $t('bf.accompaniments.grade') }}</p>
      <p class="font-weight-black">{{totalSum.toFixed(2)}}</p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'OverallGrade',
  props: {
    selectedBaseDataQuestionnaires: Array,
    sections: Array,
    baseDataRatings: Array,
    localTopics: Object,
  },
  computed: {
    ...mapState('begleitfahrt', ['answerById']),
    /**
     * Calculates the average grade of selected ratings.
     *
     * @returns {number|null} The average grade of selected ratings.
     */
    averageAccompaniments() {
      const selectedRatings = this.filteredRatings;
      const sumRatings = selectedRatings.reduce((sum, rating) => {
        const numericGrade = parseFloat(rating.grade);
        return sum + (isNaN(numericGrade) ? 0 : numericGrade);
      }, 0);
      return selectedRatings.length > 0 ? sumRatings / selectedRatings.length : null;
    },
    totalSum() {
      // Sum up all the numeric answers from each topic in sections
      const answersSum = this.sections.reduce((sum, section) => {
        return sum + section.topics.reduce((sectionSum, topic) => {
          const topicSum = this.additionNumericAnswers(topic.id);
          return topicSum !== null ? sectionSum + topicSum.sum : sectionSum;
        }, 0);
      }, 0);

      // Number of numeric answers
      const numberOfAnswers = this.sections.reduce((sum, section) => {
        return sum + section.topics.reduce((sectionSum, topic) => {
          const addition = this.additionNumericAnswers(topic.id);
          return addition !== null ? sectionSum + addition.numericAnswerCount : sectionSum;
        }, 0);
      }, 0);

      const averageAccompaniments = this.averageAccompaniments || 0;
      let hasAverageAccompaniments = this.averageAccompaniments > 0 || this.averageAccompaniments !== null ? 1 : 0;

      // Calculate the overall average
      return numberOfAnswers > 0 ? ((averageAccompaniments + answersSum) / (numberOfAnswers + hasAverageAccompaniments)) : 0;
    },
    filteredRatings() {
      return this.baseDataRatings.filter((rating) => this.selectedBaseDataQuestionnaires.includes(rating.id));
    }
  },
  methods: {
    getNonEmptyAnswers(topicId) {
      const questions = this.localTopics[topicId].questions;
      return Object.entries(questions).reduce((nonEmptyAnswers, [key, value]) => {
        if (value.answer !== '') {
          nonEmptyAnswers[key] = value;
        }
        return nonEmptyAnswers;
      }, {});
    },
    averageNumericAnswers(topicId) {
      const addition = this.additionNumericAnswers(topicId);
      const average = addition ? (addition.sum / addition.numericAnswerCount) : null;
      return average;
    },
    additionNumericAnswers(topicId){
      const nonEmptyAnswers = this.getNonEmptyAnswers(topicId);
      let hasNumbers = false;
      let numericAnswerCount = 0;
      const sum = Object.values(nonEmptyAnswers).reduce((accumulatedSum, { answer }) => {
        const answerValue = this.answerById && this.answerById[answer] && this.answerById[answer].answer;
        const numericValue = parseFloat(answerValue);
        if (!isNaN(numericValue)) {
          hasNumbers = true;
          numericAnswerCount += 1;
          return accumulatedSum + numericValue;
        }
        return accumulatedSum;
      }, 0);
      return hasNumbers ? {sum: sum, numericAnswerCount: numericAnswerCount} : null;
    }
  },
};
</script>

<style scoped>
.theme--light .list-odd-highlight  {
  background-color: #EEEEEE;
}

.theme--dark .list-odd-highlight  {
  background-color: #282828;
}

.overall-grade-container {
  max-height: 100%;
  overflow-y: auto;
}

.topic-container p {
  margin: 0;
}

.topic-container {
  padding: 0.5em 2em;
  border-radius: 10px;
}

.section-container p:nth-child(1) {
  margin: 0;
}

.caption {
  margin: 0;
}
</style>
